import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { ServerPagination } from "@/components/ui/server-pagination";
import { StudentProfileDialog } from "@/components/administration/StudentProfileDialog";
import { exportToCsv } from "@/lib/exportCsv";
import { Search, Download, X, AlertCircle, Eye } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type Eligibility = "eligible" | "not_eligible" | "on_determination";

function deriveEligibility(student: any): Eligibility {
  if (!student) return "on_determination";
  if (student.status !== "active") return "not_eligible";
  if (student.financial_clearance === true) return "eligible";
  return "on_determination";
}

function EligibilityBadge({ value }: { value: Eligibility }) {
  if (value === "eligible") {
    return <Badge className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">Eligible</Badge>;
  }
  if (value === "not_eligible") {
    return <Badge variant="destructive">Not Eligible</Badge>;
  }
  return <Badge variant="secondary">On Determination</Badge>;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100];

export function AcademicEligibilityRoster() {
  const { toast } = useToast();
  const currentYear = new Date().getFullYear();
  const defaultYear = `${currentYear}/${currentYear + 1}`;

  const [academicYear, setAcademicYear] = useState<string>(defaultYear);
  const [classFilter, setClassFilter] = useState<string>("all");
  const [semester, setSemester] = useState<string>("all");
  const [courseId, setCourseId] = useState<string>("all");
  const [eligibility, setEligibility] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [profileStudent, setProfileStudent] = useState<any>(null);

  // Distinct academic years
  const { data: years = [] } = useQuery({
    queryKey: ["elig-years"],
    queryFn: async () => {
      const { data } = await supabase.from("registrations").select("academic_year").limit(5000);
      const set = new Set<string>((data ?? []).map((r: any) => r.academic_year).filter(Boolean));
      const arr = Array.from(set).sort().reverse();
      if (!arr.includes(defaultYear)) arr.unshift(defaultYear);
      return arr;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Distinct classes (from courses)
  const { data: classes = [] } = useQuery({
    queryKey: ["elig-classes"],
    queryFn: async () => {
      const { data } = await supabase.from("courses").select("class").not("class", "is", null);
      const set = new Set<string>((data ?? []).map((r: any) => r.class).filter(Boolean));
      return Array.from(set).sort();
    },
    staleTime: 5 * 60 * 1000,
  });

  // Distinct semesters
  const { data: semesters = [] } = useQuery({
    queryKey: ["elig-semesters"],
    queryFn: async () => {
      const { data } = await supabase.from("registrations").select("semester").limit(5000);
      const set = new Set<string>((data ?? []).map((r: any) => r.semester).filter(Boolean));
      return Array.from(set).sort();
    },
    staleTime: 5 * 60 * 1000,
  });

  // Courses filtered by class
  const { data: courseOptions = [] } = useQuery({
    queryKey: ["elig-courses", classFilter],
    queryFn: async () => {
      let q = supabase.from("courses").select("id, course_code, course_name, class").order("course_code");
      if (classFilter !== "all") q = q.eq("class", classFilter);
      const { data } = await q;
      return data ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  // Build a query (used for both list + count + export)
  const buildQuery = (selectClause: string, opts?: { count?: "exact" | "planned" | "estimated"; head?: boolean }) => {
    let q = supabase.from("registrations").select(selectClause, opts as any);
    q = q.eq("academic_year", academicYear);
    if (semester !== "all") q = q.eq("semester", semester);
    if (courseId !== "all") q = q.eq("course_id", courseId);

    // Class filter — filter via embedded courses
    if (classFilter !== "all") q = q.eq("courses.class", classFilter);

    // Eligibility — translate to students.status / financial_clearance
    if (eligibility === "eligible") {
      q = q.eq("students.status", "active").eq("students.financial_clearance", true);
    } else if (eligibility === "not_eligible") {
      q = q.neq("students.status", "active");
    } else if (eligibility === "on_determination") {
      q = q.eq("students.status", "active").eq("students.financial_clearance", false);
    }

    // Search — name or registration number
    if (search.trim()) {
      const s = `%${search.trim()}%`;
      q = q.or(
        `first_name.ilike.${s},last_name.ilike.${s},student_number.ilike.${s},nactevet_reg_number.ilike.${s}`,
        { foreignTable: "students" }
      );
    }
    return q;
  };

  // Total registrations for chosen year (for empty-year guard)
  const { data: yearTotal } = useQuery({
    queryKey: ["elig-year-total", academicYear],
    queryFn: async () => {
      const { count } = await supabase
        .from("registrations")
        .select("id", { count: "exact", head: true })
        .eq("academic_year", academicYear);
      return count ?? 0;
    },
  });

  // Roster query
  const queryKey = ["elig-roster", academicYear, classFilter, semester, courseId, eligibility, search, page, pageSize];
  const { data: rosterResp, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      const select = `
        id, semester, academic_year, status,
        students!inner(id, first_name, last_name, student_number, nactevet_reg_number, status, financial_clearance, class, program, user_id, form_iv_index, nta_level, email, phone),
        courses!inner(id, course_code, course_name, class)
      `;
      const { data, error, count } = await buildQuery(select, { count: "exact" })
        .order("created_at", { ascending: false })
        .range(from, to);
      if (error) throw error;
      return { rows: data ?? [], count: count ?? 0 };
    },
    staleTime: 5 * 60 * 1000,
  });

  const rows = rosterResp?.rows ?? [];
  const totalCount = rosterResp?.count ?? 0;

  const isFiltered =
    classFilter !== "all" || semester !== "all" || courseId !== "all" || eligibility !== "all" || search.trim() !== "";

  const clearFilters = () => {
    setClassFilter("all");
    setSemester("all");
    setCourseId("all");
    setEligibility("all");
    setSearch("");
    setPage(1);
  };

  const handleDownload = async () => {
    try {
      const select = `
        semester, academic_year,
        students!inner(first_name, last_name, student_number, nactevet_reg_number, status, financial_clearance),
        courses!inner(course_code, course_name, class)
      `;
      // Fetch in chunks of 1000
      const all: any[] = [];
      let from = 0;
      const chunk = 1000;
      while (true) {
        const { data, error } = await buildQuery(select).range(from, from + chunk - 1);
        if (error) throw error;
        const batch = data ?? [];
        all.push(...batch);
        if (batch.length < chunk) break;
        from += chunk;
      }
      const headers = [
        "SN", "NS Number", "Student Name", "Class", "Semester",
        "Course Code", "Course Name", "Academic Year", "Eligibility",
      ];
      const csvRows = all.map((r: any, i: number) => {
        const s = r.students;
        const c = r.courses;
        const elig = deriveEligibility(s);
        const name = `${s?.first_name ?? ""} ${s?.last_name ?? ""}`.trim();
        const ns = s?.nactevet_reg_number || s?.student_number || "";
        const eligLabel = elig === "eligible" ? "Eligible" : elig === "not_eligible" ? "Not Eligible" : "On Determination";
        return [
          String(i + 1), ns, name, c?.class ?? "", r.semester ?? "",
          c?.course_code ?? "", c?.course_name ?? "", r.academic_year ?? "", eligLabel,
        ];
      });
      const today = new Date().toISOString().slice(0, 10);
      const fname = `academic-eligibility-${academicYear.replace("/", "-")}-${classFilter}-${semester}-${today}.csv`;
      exportToCsv(fname, headers, csvRows);
      toast({ title: "Export ready", description: `${csvRows.length} rows exported.` });
    } catch (e: any) {
      toast({ title: "Export failed", description: e.message, variant: "destructive" });
    }
  };

  const startIndex = (page - 1) * pageSize;

  return (
    <TooltipProvider>
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Academic Eligibility</CardTitle>
            <CardDescription>
              Roster of registered students with eligibility derived from active status and financial clearance.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Filter bar */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
              <div>
                <Label className="text-xs">Academic Year</Label>
                <Select value={academicYear} onValueChange={(v) => { setAcademicYear(v); setPage(1); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Class</Label>
                <Select value={classFilter} onValueChange={(v) => { setClassFilter(v); setCourseId("all"); setPage(1); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Classes</SelectItem>
                    {classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Semester</Label>
                <Select value={semester} onValueChange={(v) => { setSemester(v); setPage(1); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Semesters</SelectItem>
                    {semesters.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Course / Module</Label>
                <Select value={courseId} onValueChange={(v) => { setCourseId(v); setPage(1); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Courses</SelectItem>
                    {courseOptions.map((c: any) => (
                      <SelectItem key={c.id} value={c.id}>{c.course_code} — {c.course_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Eligibility</Label>
                <Select value={eligibility} onValueChange={(v) => { setEligibility(v); setPage(1); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="eligible">Eligible</SelectItem>
                    <SelectItem value="not_eligible">Not Eligible</SelectItem>
                    <SelectItem value="on_determination">On Determination</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Name or NS number"
                    className="pl-9"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {isFiltered && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    <X className="h-4 w-4 mr-1" /> Clear filters
                  </Button>
                )}
                <span className="text-sm text-muted-foreground">{totalCount} students</span>
              </div>
              <Button variant="outline" size="sm" onClick={handleDownload} disabled={totalCount === 0}>
                <Download className="h-4 w-4 mr-2" /> Download CSV
              </Button>
            </div>

            {yearTotal === 0 && (
              <div className="flex items-center gap-2 p-3 rounded-md bg-muted text-sm">
                <AlertCircle className="h-4 w-4 text-muted-foreground" />
                No registrations exist for {academicYear}. Pick another academic year.
              </div>
            )}

            {/* Table */}
            <div className="table-responsive">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">SN</TableHead>
                    <TableHead>NS Number</TableHead>
                    <TableHead>Student Name</TableHead>
                    <TableHead>Class</TableHead>
                    <TableHead>Semester</TableHead>
                    <TableHead>Course</TableHead>
                    <TableHead>Academic Year</TableHead>
                    <TableHead>Eligibility</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={`sk-${i}`}>
                      {Array.from({ length: 9 }).map((__, j) => (
                        <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {!isLoading && rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-10 text-muted-foreground">
                        No students match the current filters.
                        {isFiltered && (
                          <Button variant="link" className="ml-1" onClick={clearFilters}>Clear filters</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoading && rows.map((r: any, i: number) => {
                    const s = r.students;
                    const c = r.courses;
                    const elig = deriveEligibility(s);
                    const name = `${s?.first_name ?? ""} ${s?.last_name ?? ""}`.trim() || "—";
                    const ns = s?.nactevet_reg_number || s?.student_number || "—";
                    return (
                      <TableRow key={r.id}>
                        <TableCell>{startIndex + i + 1}</TableCell>
                        <TableCell className="font-mono text-xs">{ns}</TableCell>
                        <TableCell className="font-medium">{name}</TableCell>
                        <TableCell>{c?.class ?? "—"}</TableCell>
                        <TableCell>{r.semester ?? "—"}</TableCell>
                        <TableCell>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="font-mono text-xs cursor-help">{c?.course_code ?? "—"}</span>
                            </TooltipTrigger>
                            <TooltipContent>{c?.course_name ?? "—"}</TooltipContent>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{r.academic_year ?? "—"}</TableCell>
                        <TableCell><EligibilityBadge value={elig} /></TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => setProfileStudent(s)}>
                            <Eye className="h-4 w-4 mr-1" /> View Profile
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <ServerPagination
              currentPage={page}
              pageSize={pageSize}
              totalCount={totalCount}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          </CardContent>
        </Card>

        <StudentProfileDialog
          open={!!profileStudent}
          onOpenChange={(o) => !o && setProfileStudent(null)}
          student={profileStudent}
        />
      </div>
    </TooltipProvider>
  );
}
